import { ENTITY_SLOTS } from '@hoopoe/shared';
import { createFileRoute } from '@tanstack/react-router';
import { AlertTriangle, CheckCircle2, Inbox, XCircle } from 'lucide-react';
import { useState } from 'react';
import { DataBlock, DataNumber, DataText } from '../components/data/data-text';
import { ENTITY_KINDS, EntityMark, type ReviewStatus } from '../components/data/entity-mark';
import { DateInput } from '../components/guidance/date-input';
import { EmptyState } from '../components/guidance/empty-state';
import { EntityAutocomplete } from '../components/guidance/entity-autocomplete';
import { Field } from '../components/guidance/field';
import { Tip } from '../components/guidance/tip';
import { PageHeader } from '../components/layout/app-shell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { searchSampleEntities } from '../lib/sample-entities';

export const Route = createFileRoute('/design')({ component: Design });

const SAMPLE_NAMES = [
  { name: 'Hasan Haddad', lang: 'en' },
  { name: 'حسن حداد', lang: 'ar' },
  { name: 'Hasan Haddad (حسن حداد)', lang: 'en' },
  { name: 'ليلى نصار — Layla Nassar', lang: 'ar' },
  { name: 'Forum Méridien', lang: 'fr' },
];
const GRAYS = {
  50: 'bg-gray-50',
  100: 'bg-gray-100',
  200: 'bg-gray-200',
  300: 'bg-gray-300',
  400: 'bg-gray-400',
  500: 'bg-gray-500',
  600: 'bg-gray-600',
  700: 'bg-gray-700',
  800: 'bg-gray-800',
  900: 'bg-gray-900',
  950: 'bg-gray-950',
};
const SLOTS = Array.from({ length: ENTITY_SLOTS }, (_, i) => i + 1);
const STATUSES: ReviewStatus[] = ['verified', 'unreviewed', 'disputed', 'rejected'];

/** Design reference: tokens, entity marks and mixed-direction data rendering (PLAN §12.1–12.2). */
function Design() {
  return (
    <div className="space-y-10">
      <PageHeader title="Design reference" description="Grey interface, color reserved for data and states." />

      <section aria-labelledby="bidi">
        <h2 id="bidi" className="mb-3 text-lg font-semibold">
          Mixed-direction data
        </h2>
        <table className="w-full max-w-2xl border-collapse text-sm">
          <thead>
            <tr className="border-b text-start text-muted-foreground">
              <th className="py-2 text-start font-medium">Name</th>
              <th className="py-2 text-start font-medium">Updated</th>
              <th className="py-2 text-start font-medium">Amount</th>
            </tr>
          </thead>
          <tbody>
            {SAMPLE_NAMES.map((s, i) => (
              <tr key={s.name} className="border-b">
                <td className="py-2">
                  <span className="inline-flex items-center gap-2">
                    <EntityMark kind="actor_person" colorSlot={i + 1} />
                    <DataText lang={s.lang} data-testid={`name-${i}`}>
                      {s.name}
                    </DataText>
                  </span>
                </td>
                <td className="py-2">
                  <DataNumber>2024-05-17</DataNumber>
                </td>
                <td className="py-2">
                  <DataNumber>€1,200,000</DataNumber>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <DataBlock lang="ar" className="mt-4 max-w-2xl rounded-md border bg-surface p-3" data-testid="quote-ar">
          قال حسن حداد إن «الحوار ضروري» خلال قمة ميريديان ٢٠٢٤.
        </DataBlock>
      </section>

      <section aria-labelledby="entities">
        <h2 id="entities" className="mb-3 text-lg font-semibold">
          Entity palette, shapes and review outlines
        </h2>
        <div className="flex flex-wrap gap-3">
          {SLOTS.map(slot => (
            <span key={slot} className="inline-flex items-center gap-1.5 text-sm">
              <EntityMark kind="actor_org" colorSlot={slot} size={20} why={`Type color slot ${slot}`} />
              <DataNumber>{slot}</DataNumber>
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5 text-sm">
            <EntityMark kind="actor_org" colorSlot={null} size={20} why="No color: fallback grey" />
            none
          </span>
        </div>
        <div className="mt-4 flex flex-wrap gap-4 text-sm">
          {ENTITY_KINDS.map(k => (
            <span key={k} className="inline-flex items-center gap-1.5">
              <EntityMark kind={k} colorSlot={1} size={20} /> {k}
            </span>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-4 text-sm">
          {STATUSES.map(s => (
            <span key={s} className="inline-flex items-center gap-1.5">
              <EntityMark kind="actor_person" colorSlot={7} status={s} size={20} /> {s}
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5">
            <EntityMark kind="document" colorSlot={3} confidential size={20} /> confidential
          </span>
        </div>
      </section>

      <Guidance />

      <section aria-labelledby="ui">
        <h2 id="ui" className="mb-3 text-lg font-semibold">
          Interface greys and states
        </h2>
        <div className="flex flex-wrap gap-1">
          {Object.entries(GRAYS).map(([g, cls]) => (
            <div key={g} className="w-16 text-center text-xs text-muted-foreground">
              <div className={`h-10 rounded border ${cls}`} />
              {g}
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button>Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="danger">Delete</Button>
        </div>
        <ul className="mt-4 space-y-1 text-sm">
          <li className="flex items-center gap-2 text-danger-foreground">
            <XCircle className="size-4" aria-hidden="true" /> Error: upload failed
          </li>
          <li className="flex items-center gap-2 text-warning-foreground">
            <AlertTriangle className="size-4" aria-hidden="true" /> Warning: taking longer than usual
          </li>
          <li className="flex items-center gap-2 text-success-foreground">
            <CheckCircle2 className="size-4" aria-hidden="true" /> Success: ready for review
          </li>
        </ul>
      </section>
    </div>
  );
}

function Guidance() {
  const [picked, setPicked] = useState<string | null>(null);
  return (
    <section aria-labelledby="guidance" className="max-w-xl space-y-5">
      <h2 id="guidance" className="text-lg font-semibold">
        Guidance kit
      </h2>
      <Tip id="design-accept-block" learnMore="review-an-upload">
        Accept a whole block when the sheet is trusted.
      </Tip>
      <Field
        guidance={{
          key: 'full_name',
          label: 'Full name',
          help: 'The name as written in the source, in its original script.',
          placeholder: 'e.g. Hasan Haddad',
          learnMore: 'cross-language-names',
          required: true,
        }}
      >
        {p => <Input {...p} />}
      </Field>
      <Field
        guidance={{
          key: 'date_of_birth',
          label: 'Date of birth',
          help: 'Exact or approximate. Keep the precision the source gives.',
          placeholder: "e.g. 1971-03-02 or 'March 1971'",
          formatHint: 'Accepts 2019-03, March 2019, c. 2021, 2017–2024, since 2021.',
        }}
      >
        {p => <DateInput {...p} />}
      </Field>
      <Field
        guidance={{
          key: 'employer',
          label: 'Employer',
          help: 'Another actor. Type any name, alias or script.',
          placeholder: 'e.g. Meridian Policy Forum or حسن',
        }}
      >
        {p => (
          <EntityAutocomplete
            {...p}
            search={searchSampleEntities}
            onSelect={e => setPicked(e.name)}
            onCreate={q => setPicked(`new: ${q}`)}
          />
        )}
      </Field>
      <p className="text-sm" data-testid="picked">
        {picked ? `Selected: ${picked}` : 'Nothing selected'}
      </p>
      <EmptyState icon={Inbox} title="Review inbox is empty" learnMore="review-statuses">
        New uploads that need a person's check appear here.
      </EmptyState>
    </section>
  );
}
